import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'

import LoadProgress from '@/components/LoadProgress'

import { DataGridPremium, GridColDef, GridPaginationModel } from '@mui/x-data-grid-premium'

import { memberRegistrationRecordsQueryOptions } from '@/api/mem/memberRegistrationQueryOptions'
import useBasicGridColumns from '@/components/grid/BasicGridColumns'
import useBasicValueOptions from '@/components/grid/BasicValueOptions'
import { DetailGridToolbar } from '@/components/mui-x/datagrid/DetailGridToolbar'
import { getCurrentMember } from '@/store/nav/navSlice'
import dayjs from 'dayjs'
import { t } from 'i18next'
import React from 'react'
import { useSelector } from 'react-redux'

export const Route = createFileRoute(
    '/_auth/member_/$memberUuid/registrationRecords',
)({
    component: MemberComponent,
})

function MemberComponent() {
    const params = Route.useParams()
    const memberRegistrationRecordsQuery = useSuspenseQuery(
        memberRegistrationRecordsQueryOptions(params.memberUuid),
    )
    const memberRegistrationRecords = memberRegistrationRecordsQuery.data

    const memberRegistrationSnapshotUuids = [...new Set(memberRegistrationRecords.map((record) => {
        return record.snapshot.uuid
    }))]

    const memberRegistrationSnapshots = memberRegistrationSnapshotUuids.map((uuid) => {
        return memberRegistrationRecords.find((record) => {
            return record.snapshot.uuid === uuid
        })?.snapshot

    })

    // console.log(memberRegistrationSnapshots)

    const rows = [...memberRegistrationSnapshots, ...memberRegistrationRecords]

    const basicGridColumns = useBasicGridColumns()
    const basicValueOptions = useBasicValueOptions()

    // const {xsmall, small, medium, large, xlarge} = useResponsive();

    const columns: GridColDef[] = []
    columns.push(
        {
            field: 'scoutId',
            headerName: t('property.member.scoutId'),
            minWidth: 150,
        },
        {
            field: 'firstname',
            headerName: t('property.member.firstname'),
            minWidth: 150,
        },
        {
            field: 'lastname',
            headerName: t('property.member.lastname'),
            minWidth: 150,
        },
        basicGridColumns.scoutstateColumn,
        { field: 'street', headerName: t('property.member.street'), minWidth: 150 },
        {
            field: 'postcode',
            headerName: t('property.member.postcode'),
            minWidth: 150,
        },
        { field: 'city', headerName: t('property.member.city'), minWidth: 150 },
        {
            field: 'emailPrimary',
            headerName: t('property.member.emailPrimary'),
            minWidth: 150,
        },
        {
            field: 'troopName',
            headerName: t('property.registrationRecord.troopName'),
            minWidth: 150,
        },
        {
            field: 'divisionName',
            headerName: t('property.registrationRecord.divisionName'),
            minWidth: 150,
        },
        {
            field: 'sectionMedusaAbbreviation',
            headerName: t('property.registrationRecord.sectionMedusaAbbreviation'),
            minWidth: 150,
        },
        {
            field: 'troopJobs',
            headerName: t('property.registrationRecord.troopJobs'),
            minWidth: 150,
        },
        {
            field: 'divisionJobs',
            headerName: t('property.registrationRecord.divisionJobs'),
            minWidth: 150,
        },
        {
            field: 'federationJobs',
            headerName: t('property.registrationRecord.federationJobs'),
            minWidth: 150,
        },
        {
            field: 'troopPayment',
            headerName: t('property.registrationRecord.troopPayment'),
            minWidth: 150,
            type: 'singleSelect',
            valueOptions: basicValueOptions.paymentOptions,
        },
        {
            field: 'divisionPayment',
            headerName: t('property.registrationRecord.divisionPayment'),
            minWidth: 150,
            type: 'singleSelect',
            valueOptions: basicValueOptions.paymentOptions,
        },
        {
            field: 'federationPayment',
            headerName: t('property.registrationRecord.federationPayment'),
            minWidth: 150,
            type: 'singleSelect',
            valueOptions: basicValueOptions.paymentOptions,
        },
    )

    const currentMember = useSelector(getCurrentMember)

    const calculatedPageSize = Math.floor((window.innerHeight - 220) / 40);
    const pageSize = calculatedPageSize;

    const defaultPageSizeOptions = [{ value: 10, label: '10' }, { value: 25, label: '25' }, { value: 50, label: '50' }, { value: 100, label: '100' }, { value: 250, label: '250' }]
    defaultPageSizeOptions.push({ value: calculatedPageSize, 'label': 'Optimal' })

    if (defaultPageSizeOptions.filter((value) => value.value == pageSize).length === 0) {
        defaultPageSizeOptions.push({ value: pageSize, label: pageSize.toString() })
    }

    const pageSizeOptions = Array.from(new Set(defaultPageSizeOptions))
    pageSizeOptions.sort((a, b) => a.value - b.value)
    const [paginationModel, setPaginationModel] = React.useState<GridPaginationModel>({
        page: 0,
        pageSize: pageSize,
    })

    const handlePaginationModelChange = (model: GridPaginationModel) => {
        setPaginationModel(model);
    }

    const CustomToolbar = () => {
        return (
            <DetailGridToolbar scope='registration' getScopeName={() => { return currentMember.firstname + "_" + currentMember.lastname }} />
        )
    }

    const groupingColDef = {
        headerName: t('entity.registrationSnapshot'),
        minWidth: 300,
        valueGetter: (_value: string, row: any) => {
            if (row.type) {
                return t('enum.registrationSnapshotType.' + row.type) + ' - ' + dayjs(row.createDate).format('DD.MM.YYYY')
            } else {
                return row.troopName || row.divisionName || row.federationName
            }
        },
    }

    if (
        memberRegistrationRecordsQuery.isLoading ||
        memberRegistrationRecordsQuery.isFetching
    ) {
        return <LoadProgress />
    }

    return (
        <>
            {/*JSON.stringify({
          xs: xsmall,
          sm: small,
          md: medium, 
          lg: large,
          xl: xlarge,
        })*/}
            <DataGridPremium
                treeData
                getTreeDataPath={(row) => row.snapshot ? [row.snapshot.uuid, row.uuid] : [row.uuid]}
                groupingColDef={groupingColDef}
                rows={rows}
                columns={columns}
                getRowId={(row) => {
                    return row.uuid || ''
                }}
                initialState={{
                    columns: {
                        columnVisibilityModel: {
                            scoutId: false,
                            firstname: false,
                            lastname: false,
                            emailPrimary: false,
                            street: false,
                            postcode: false,
                            city: false,
                            troopName: false,
                        },
                    },
                }}
                slots={{ toolbar: CustomToolbar }}
                pagination
                paginationModel={paginationModel}
                pageSizeOptions={pageSizeOptions}
                onPaginationModelChange={handlePaginationModelChange}
                disableAggregation
                disableRowGrouping
                disableColumnPinning
                disableColumnReorder
                disableDensitySelector
                density='compact'
                showToolbar />
        </>
    );
}
